import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Body2, Caption } from '../../components/Typography/Typography';
import {
    BUSINESS_EMAILS_REGEX,
    EMAIL_REGEX,
    TELEPHONE_REGEX,
} from '../../../config/constants';

import Select from '../../components/Select/Select';
import Box from '../../components/Box/Box';
import Field from '../../components/Field/Field';
import { rem } from 'polished';
import { css } from 'styled-components';

const typeMapper = (excludePersonalEmailDomains) => ({
    email: {
        pattern: excludePersonalEmailDomains
            ? BUSINESS_EMAILS_REGEX
            : EMAIL_REGEX,
    },
    tel: {
        pattern: TELEPHONE_REGEX,
        maxLength: 12,
    },
});

const InputSlots: React.FC = ({
    inputs,
    extendProps,
    excludePersonalEmailDomains = false,
    wrapFirstTwoFields = false,
}) => {
    const [countryList, setCountryList] = useState([]);
    useEffect(() => {
        const hasCountryInput = inputs.find(
            (input) => input.type === 'country'
        );
        const countriesAlreadyLoaded = Boolean(countryList.length);
        if (hasCountryInput && !countriesAlreadyLoaded) {
            import('countries-list').then(({ countries }) => {
                setCountryList(
                    Object.keys(countries).map((country) => ({
                        label: countries[country].name,
                        value: countries[country].name,
                    }))
                );
            });
        }
    }, [inputs, countryList.length]);

    return (
        <Box
            display="grid"
            gridTemplateColumns={wrapFirstTwoFields ? '1fr 1fr' : '1fr'}
            gridGap={rem(20)}
            css={css`
                ${wrapFirstTwoFields &&
                `>:nth-child(n+3) { grid-column: 1/-1 }`}
            `}
        >
            {inputs.map((input, i) => {
                const {
                    key,
                    label,
                    placeholder,
                    is_required,
                    type,
                    options,
                    error_message,
                } = input;
                const commonProps = {
                    key,
                    id: key,
                    name: key,
                    placeholder,
                    required: is_required,
                    ...extendProps({
                        ...input,
                        ...typeMapper(excludePersonalEmailDomains)[type],
                    }),
                };
                if (type === 'textarea')
                    return (
                        <Field id={key}>
                            <Field.Label>{label}</Field.Label>
                            <Field.Textarea resize="none" {...commonProps} />
                        </Field>
                    );

                if (type === 'dropdown' || type === 'country') {
                    const optionsList =
                        options &&
                        options.split(',').map((option) => ({
                            label: option.trim(),
                            value: option.trim(),
                        }));

                    return (
                        <Controller
                            as={Select}
                            index={i}
                            {...commonProps}
                            id={commonProps.id}
                            name={commonProps.id}
                            ref={undefined}
                            options={optionsList || countryList}
                            rules={{ required: is_required }}
                            formatOptionLabel={({ label }) => (
                                <Body2>{label}</Body2>
                            )}
                        />
                    );
                }
                return (
                    <Field id={key}>
                        <Field.Label>{label}</Field.Label>
                        <Field.Input
                            type={type}
                            label={label}
                            {...commonProps}
                        />
                        {error_message && (
                            <Field.ErrorMessage>
                                {error_message}
                            </Field.ErrorMessage>
                        )}
                    </Field>
                );
            })}
        </Box>
    );
};

export function normalizeValues(values) {
    const normalizedValues = {
        ...values,
    };
    Object.keys(normalizedValues).forEach((key) => {
        normalizedValues[key] =
            typeof normalizedValues[key] === 'object'
                ? normalizedValues[key].value
                : normalizedValues[key];
    });
    return normalizedValues;
}

export default InputSlots;
